/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useState } from 'react'
import { InputNumber, Segmented, message } from 'antd'
import { FormikProvider } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import type { InviteeInput } from '@sportninja/common/services/teamBalance'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import BaseModal from 'src/components/ts-components/BaseModal'
import Text from 'src/components/ts-components/Text'
import { PlayerPositionSelect } from 'src/components/ts-components/PlayerPositionSelect'
import {
  paragraphMedium,
  paragraphSmall,
  paragraphXSmall,
} from 'src/components/css'
import { segmentedToggleStyle } from 'src/styles/segmentedToggleStyle'
import { useInviteeForm } from '../hooks/useInviteeForm'
import {
  ADD_SLOT_TAB_LABEL_KEYS,
  addSlotTabs,
  initialAddSlotTab,
} from '../utils/addSlotTabs'
import type { AddSlotTab } from '../utils/addSlotTabs'
import { balanceLabel, balanceText } from '../utils/balanceI18n'
import InviteApprovalLock from './InviteApprovalLock'
import InviteeRows from './InviteeRows'

const MIN_SLOTS = 1
const MAX_SLOTS = 60

/**
 * `create` — the toolbar's Add Player Slots, a tab for each kind of slot: Open
 * Player Slots, Reserved Player Slots and Player Finder Slots (where the team may
 * advertise).
 * `list` — the row kebab's "List in Player Finder…": the slot already exists and
 * the intent is already "list it", so only the position is asked for.
 * `edit-position` — the row kebab's "Edit Player Finder Position" on a slot that
 * is ALREADY listed: same picker, starting from the current position. A position
 * edit, not an opt-in, so it never turns recruiting back on.
 */
export type AddPlayerSlotModalMode = 'create' | 'list' | 'edit-position'

export interface AddPlayerSlotSubmit {
  count: number
  finderListed: boolean
  finderPlayerTypeId: string | null
}

type AddPlayerSlotModalProps = {
  open: boolean
  mode: AddPlayerSlotModalMode
  /**
   * A slot write — Open Player Slots, Player Finder Slots, or a kebab mode's Save —
   * is on its way.
   */
  submitting: boolean
  /**
   * `create` — offer the Player Finder Slots tab: Canlan, and only while the
   * server's opt-in gate allows listing (D48). Choosing that tab IS the opt-in.
   */
  canAddFinderSlot?: boolean
  /**
   * `create` — Reserved Player Slots can send: the team registration is approved.
   * Before then the tab stays and says why, instead of the option going missing.
   */
  canInvitePlayers?: boolean
  /** `create` — a Reserved Player Slots batch is on its way. */
  inviting?: boolean
  /** Drives the position options; absent means no positions can be offered. */
  sportId?: string | null
  /**
   * The team is not recruiting in this competition (never has, or switched it
   * off). Listing a slot turns recruiting on — and re-advertises any other slot
   * already listed — so the two ways to list say so before the rep confirms.
   * Edit position does not change it and says nothing.
   */
  recruitingOff?: boolean
  /**
   * `edit-position` only — the position the slot recruits for now. Null on a slot
   * listed for "any player" before SN-8467: it opens on the sport's default, and
   * saving converts it.
   */
  initialPlayerTypeId?: string | null
  onSubmit: (values: AddPlayerSlotSubmit) => Promise<unknown>
  /**
   * `create` › Reserved Player Slots — one new slot held for each person. Resolves
   * when the dialog may close; rejects with the API error, which lands on the row
   * and field it names.
   */
  onInvite?: (invitees: InviteeInput[]) => Promise<unknown>
  onClose: () => void
}

const clampCount = (value: number): number =>
  Math.min(Math.max(Math.round(value || MIN_SLOTS), MIN_SLOTS), MAX_SLOTS)

/**
 * SN-4709 × SN-8081 (D46) — Add Player Slots, and the row kebab's Player Finder
 * position.
 *
 * Inon, Oct 2 — Add is one toolbar button, and the kind of slot is a tab here. It
 * replaced the Add menu (Player Slot / Player Finder Slot / Invited Player Slot),
 * which asked for a type before the rep could see what each one does, and left
 * Invited Player Slot out without a word until the team registration was
 * approved. The Reserved Player Slots tab now stays and says why. Its copy is at
 * body size, the same 16px as the footer buttons (Inon, Oct 2).
 *
 * Inon × Jeff, Oct 2 — each tab is named for the slot it adds, so Open Player
 * Slots, Reserved Player Slots and Player Finder Slots read as one family (were
 * Open Slots / Invite Players / Player Finder), with Player Finder third. Each
 * intro says what its slot does, in players, never "persons": who can take an
 * open one (not "one slot per player you expect"), that a reserved one is held
 * for one name and email, and that a Player Finder one finds eligible players.
 *
 * The Player Finder opt-in has to be ASKED, never inferred (Jeff: "you either
 * need to ask them…", "it's on a per… per slot"): the Player Finder Slots tab is
 * the question, so an open player slot is never listed and a Player Finder one
 * always is.
 *
 * SN-8467 (Jeff, Oct 1) — every listed slot names a position ("Positions
 * Needed"): the picker starts on the sport's default, and there is no "any
 * player" to clear back to. Until a position is set — the sport's positions still
 * loading, failing to load, or a team with no sport — the dialog cannot list a
 * slot; an ordinary, unlisted slot needs none.
 */
const AddPlayerSlotModal = (props: AddPlayerSlotModalProps) =>
  // Mounted only while open, so every open starts on its first tab (F6) with
  // fresh fields: a previous count, position or invitee never carries into the
  // next.
  props.open ? <AddPlayerSlotForm {...props} /> : null

const AddPlayerSlotForm = ({
  open,
  mode,
  submitting,
  canAddFinderSlot = false,
  canInvitePlayers = false,
  inviting = false,
  sportId = null,
  recruitingOff = false,
  initialPlayerTypeId = null,
  onSubmit,
  onInvite,
  onClose,
}: AddPlayerSlotModalProps) => {
  const isCreate = mode === 'create'
  const isEditPosition = mode === 'edit-position'
  // Both kebab modes act on ONE existing slot and only ask for a position.
  const isSlotMode = mode === 'list' || isEditPosition

  // F6 — opens on Reserved Player Slots when the team may invite, else Open.
  const [tab, setTab] = useState<AddSlotTab>(() =>
    initialAddSlotTab({ canInvitePlayers })
  )
  const [count, setCount] = useState<number>(MIN_SLOTS)
  // Edit position starts from what the slot recruits for now; every other mode
  // starts empty, and the picker fills in the sport's default.
  const [playerTypeId, setPlayerTypeId] = useState<number | undefined>(() => {
    const current = Number(initialPlayerTypeId)

    return isEditPosition && initialPlayerTypeId && Number.isFinite(current)
      ? current
      : undefined
  })
  const invite = useInviteeForm({
    onSubmit: (invitees) => onInvite?.(invitees) ?? Promise.resolve(),
  })

  // A Player Finder slot is listed, with a position, from the start.
  const isFinderCreate = isCreate && tab === 'player_finder'
  const isInviteTab = isCreate && tab === 'invited'

  // Whether confirming lists the slot — the same rule the payload below uses.
  const listsSlot = isSlotMode || isFinderCreate
  const positionMissing = listsSlot && playerTypeId === undefined

  const submitSlots = () => {
    if (positionMissing) {
      return
    }
    onSubmit({
      count: isSlotMode ? 1 : clampCount(count),
      finderListed: listsSlot,
      finderPlayerTypeId:
        listsSlot && playerTypeId !== undefined ? String(playerTypeId) : null,
    }).catch((error) => {
      message.error(
        (error as Error)?.message || balanceText('common:balanceAddSlotError')
      )
    })
  }

  // The button says what it will do: how many slots, or how many invites.
  const submitText = isSlotMode
    ? balanceLabel('common:save')
    : isInviteTab
    ? balanceLabel('common:balanceInviteSend', {
        count: invite.formik.values.invitees.length,
      })
    : balanceLabel(
        isFinderCreate
          ? 'common:balanceAddFinderSlotsSubmit'
          : 'common:balanceAddSlotsSubmit',
        { count: clampCount(count) }
      )

  const resumesRecruiting = recruitingOff && (mode === 'list' || isFinderCreate)

  // Said BEFORE the rep confirms: listing is also what switches recruiting back on.
  const resumeHint = resumesRecruiting ? (
    <p css={slotIntroStyle} data-testid='add-slot-finder-resumes'>
      {balanceText('common:balanceListInFinderResumes')}
    </p>
  ) : null

  const positionPicker = (
    <div>
      <PlayerPositionSelect
        value={playerTypeId}
        onChange={setPlayerTypeId}
        sportId={sportId ?? undefined}
        name='finder_player_type_id'
        label={balanceLabel('common:balanceFinderPositionLabel')}
        preselectDefault
      />
    </div>
  )

  const countField = (
    <div css={fieldStyle} data-testid='add-slot-count'>
      <Text variant='paragraphMedium' weight='regular' color={colors.WHITE}>
        {balanceLabel('common:balanceAddSlotCount')}
      </Text>
      <div css={countRowStyle}>
        <InputNumber
          id='add-slot-count'
          aria-label={balanceLabel('common:balanceAddSlotCount')}
          min={MIN_SLOTS}
          max={MAX_SLOTS}
          value={count}
          onChange={(value) => setCount(clampCount(Number(value ?? MIN_SLOTS)))}
          css={numberInputStyle}
        />
        <span css={hintStyle}>
          {balanceText('common:balanceAddSlotCountHint')}
        </span>
      </div>
    </div>
  )

  const invitePanel = canInvitePlayers ? (
    <>
      <p css={tabIntroStyle}>
        {balanceText('common:balanceInviteModalIntroCreate')}
      </p>
      {invite.formMessage ? (
        <p css={formErrorStyle} role='alert' data-testid='invite-error'>
          {invite.formMessage}
        </p>
      ) : null}
      <FormikProvider value={invite.formik}>
        <InviteeRows />
      </FormikProvider>
    </>
  ) : (
    // An unapproved team can't take players yet: the tab says so rather than
    // going missing, and Send stays off.
    <InviteApprovalLock
      reason={balanceText('common:balanceInviteNeedsApproval')}
    />
  )

  return (
    <BaseModal
      isOpen={open}
      onClose={onClose}
      title={balanceLabel(
        isEditPosition
          ? 'common:balanceEditFinderPosition'
          : mode === 'list'
          ? 'common:balanceListInFinder'
          : 'common:balanceAddSlotTitle'
      )}
      // Wide enough for each tab's one sentence at 16px, and a person's three
      // fields on one line.
      desktopWidth={isCreate ? '700px' : '520px'}
      destroyOnClose
      actions={
        <div css={footerStyle}>
          <HeaderActionButton
            variant='secondary'
            text={balanceLabel('common:cancel')}
            onClick={onClose}
            disabled={submitting || inviting}
            data-testid='add-slot-cancel'
          />
          <HeaderActionButton
            variant='primary'
            text={submitText}
            onClick={
              isInviteTab
                ? () => {
                    invite.formik.submitForm()
                  }
                : submitSlots
            }
            disabled={isInviteTab ? !canInvitePlayers : positionMissing}
            isLoading={
              isInviteTab ? inviting || invite.formik.isSubmitting : submitting
            }
            data-testid='add-slot-submit'
          />
        </div>
      }
    >
      {isCreate ? (
        <>
          <Segmented
            css={[segmentedToggleStyle, tabsStyle]}
            aria-label={balanceText('common:balanceAddTabsLabel')}
            value={tab}
            onChange={(value) => setTab(value as AddSlotTab)}
            options={addSlotTabs({ canAddFinderSlot }).map((key) => ({
              value: key,
              label: balanceLabel(ADD_SLOT_TAB_LABEL_KEYS[key]),
            }))}
          />
          <div css={tabPanelStyle}>
            {isInviteTab ? (
              invitePanel
            ) : (
              <>
                {/*
                  C4 — the finder card's only action is "View & Register", so the
                  Player Finder copy must NOT promise that anyone will reach out.
                */}
                <p
                  css={tabIntroStyle}
                  data-testid={
                    isFinderCreate ? 'add-slot-finder-help' : undefined
                  }
                >
                  {balanceText(
                    isFinderCreate
                      ? 'common:balanceListInFinderHelp'
                      : 'common:balanceAddSlotIntro'
                  )}
                </p>
                {countField}
                {isFinderCreate ? positionPicker : null}
                {resumeHint}
              </>
            )}
          </div>
        </>
      ) : (
        <>
          {/* The intent is already "list it", so the position picker stands alone. */}
          <p css={slotIntroStyle}>
            {balanceText(
              isEditPosition
                ? 'common:balanceEditFinderPositionIntro'
                : 'common:balanceListInFinderIntro'
            )}
          </p>
          {positionPicker}
          {resumeHint}
        </>
      )}
    </BaseModal>
  )
}

export default AddPlayerSlotModal

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`
/**
 * The tab labels at body size; the house toggle's 34px items (a 40px control
 * with its track) stay as they are.
 */
const tabsStyle = css`
  .ant-segmented-item-label {
    ${paragraphMedium};
    line-height: 34px;
  }
`
/**
 * Each tab is as tall as its own content. A shared minimum height (so switching
 * tabs never moved the footer) left a gap under Open Player Slots' one field
 * (Inon, Oct 2).
 */
const tabPanelStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
/** One short sentence per tab, at body size: what this kind of slot does. */
const tabIntroStyle = css`
  ${paragraphMedium};
  margin: 0;
  color: ${colors.NEUTRAL_50};
  line-height: 1.5;
`
const slotIntroStyle = css`
  ${paragraphXSmall};
  margin: 0;
  color: ${colors.NEUTRAL_40};
  line-height: 1.5;
`
const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`
/** The count and its range share a line: the range is a footnote to the field. */
const countRowStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
`
/** House field treatment — matches TextInput so the modal reads as one form. */
const numberInputStyle = css`
  width: 120px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};

  .ant-input-number-input {
    ${paragraphMedium};
    height: 38px;
  }
`
const hintStyle = css`
  ${paragraphSmall};
  color: ${colors.NEUTRAL_70};
`
const formErrorStyle = css`
  ${paragraphSmall};
  margin: 0;
  color: ${colors.ERROR_200};
  line-height: 1.5;
`
